import { waitForLogin, listenToLogout } from './loginHandler';
import { IFRAME_SOURCE_ID, MESSAGE_EVENT_TYPE, SPINNER_WRAPPER_ID } from '../constants';
import { createDeviceInfoFromCookies } from '@sitebase/device-detector';
import postMessageHandler, { close } from './handlers';
import { createIframe, createSpinner, createWrapper, clearSocket, reloadIframe } from './view';

type StartType = {
  selector: string;
};

// Get DOM element
const getElement = (id: string): HTMLElement => {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Failed to match: ${id}`);
  return element;
};

// Disable body scroll
const disableBodyScroll = (): void => {
  const { body } = document;
  if (body) {
    body.classList.add('noScroll');
  }
};

// Enable body scroll
const enableBodyScroll = (): void => {
  const { body } = document;
  if (body) {
    body.classList.remove('noScroll');
  }
};

// Set class by device
const classByDevice = (): string => {
  const deviceInfo = createDeviceInfoFromCookies(window.document.cookie, (e) => console.log('DeviceInfo: ', e));

  return deviceInfo.isDesktop ? 'isDesktop' : deviceInfo.isTablet ? 'isTablet' : 'isMobile';
};

// Desktop Modal Overlay Listener
const listenOverlay = (): void => {
  const eventType = 'click';
  const eventCallback: EventListener = (e: MouseEvent) => {
    // avoid close when it's loading and user clicks inside the modal
    if (e.target instanceof HTMLElement && e.target.closest && e.target.closest(`#${SPINNER_WRAPPER_ID}`)) {
      return;
    }

    close();
    document.removeEventListener(eventType, eventCallback, false);
  };
  document.addEventListener(eventType, eventCallback, false);
};

const getCookie = (name): string | null => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  if (match) return match[2];

  return null;
};

const getAccountId = (): string => process.env.ACCOUNT_ID ?? getCookie('ddl_customer_account_number');

const getIframeUrl = (): string => {
  const accountId = getAccountId();

  if (!accountId) {
    throw new Error('Cannot load widget, account id not found');
  }

  return process.env.IFRAME_SRC.replace('<accountId>', accountId);
};

const openIframe = async (selector): Promise<void> => {
  const sbElement = getElement(selector);

  try {
    await waitForLogin();

    disableBodyScroll();
    createSpinner(sbElement);

    const iframeUrl = getIframeUrl();

    const { iframeWrapper, closeButton } = createWrapper(sbElement);
    createIframe(iframeWrapper, iframeUrl);

    const deviceClass = classByDevice();
    sbElement.classList.add(deviceClass);

    if (deviceClass === 'isDesktop') {
      listenOverlay();
    }

    window.addEventListener(MESSAGE_EVENT_TYPE, postMessageHandler, false);

    const closeHandler = (): void => {
      closeButton.removeEventListener('click', closeHandler);

      postMessageHandler({
        data: {
          source: IFRAME_SOURCE_ID,
          action: 'close',
          payload: {},
        },
        origin: process.env.IFRAME_SRC || '',
      });
    };

    closeButton.addEventListener('click', closeHandler);

    await listenToLogout(reloadIframe);
  } catch {
    close();
  }
};

export async function start({ selector }: StartType): Promise<void> {
  await openIframe(selector);
}

export function stop(): void {
  // implementation of custom logic cleanup
  clearSocket();
  window.removeEventListener(MESSAGE_EVENT_TYPE, postMessageHandler, false);
  enableBodyScroll();
}
